import { useCallback, useEffect, useState } from 'react';
import { getFeedback, listEscalations, type EscalationItem, type FeedbackItem } from '../api';
import type { FeedbackLocale, FeedbackTranslate } from '../i18n';
import {
  Button,
  Card,
  Checkbox,
  DataTable,
  Drawer,
  Field,
  Moment,
  Notice,
  Select,
  StatusBadge,
  type Column,
} from '../../../shell/kit';
import { useToast } from '../../../shell/toast';
import { errorText } from '../../../shell/errors';
import { FeedbackCard } from '../queue/card';

const REASONS = ['low_score', 'overdue', 'manual'] as const;

/**
 * Журнал эскалаций (М5.4, §4.5.3). Запись эскалации создавалась, но её никто
 * не читал: руководитель узнавал о ней, только случайно открыв обращение.
 * Теперь эскалации видны списком — кому ушло уведомление и почему, — а из
 * строки открывается та же карточка, что и в очереди.
 */
export function EscalationsView({
  t,
  locale,
  siteId,
  canAnswer,
}: {
  t: FeedbackTranslate;
  locale: FeedbackLocale;
  siteId: string | null;
  canAnswer: boolean;
}) {
  const toast = useToast();
  const [items, setItems] = useState<readonly EscalationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [openOnly, setOpenOnly] = useState(true);
  const [reason, setReason] = useState('');
  const [selected, setSelected] = useState<FeedbackItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = await listEscalations({
        ...(siteId ? { siteId } : {}),
        openOnly,
        ...(reason ? { reason } : {}),
      });
      setItems(page.items);
    } catch (err) {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    } finally {
      setLoading(false);
    }
  }, [siteId, openOnly, reason, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = async (row: EscalationItem): Promise<void> => {
    try {
      setSelected(await getFeedback(row.feedbackId));
    } catch (err) {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    }
  };

  const columns: ReadonlyArray<Column<EscalationItem>> = [
    { key: 'when', header: t('feedback.esc.when'), render: (r) => <Moment iso={r.createdAt} /> },
    {
      key: 'reason',
      header: t('feedback.esc.reason'),
      render: (r) => (
        <StatusBadge tone={r.reason === 'low_score' ? 'danger' : r.reason === 'overdue' ? 'warn' : 'neutral'}>
          {t(`feedback.esc.reason.${r.reason}` as never)}
        </StatusBadge>
      ),
    },
    { key: 'site', header: t('feedback.reports.site'), render: (r) => r.siteName ?? '—' },
    {
      key: 'score',
      header: t('feedback.queue.score'),
      render: (r) => (r.score === null ? '—' : <span className={r.score <= 3 ? 'font-semibold text-danger' : ''}>{r.score}</span>),
    },
    {
      key: 'text',
      header: t('feedback.card.text'),
      render: (r) => <span className="line-clamp-2 text-xs text-ink-muted">{r.note && r.reason === 'manual' ? r.note : (r.excerpt ?? '—')}</span>,
    },
    {
      key: 'to',
      header: t('feedback.esc.to'),
      render: (r) => r.toEmployeeName ?? <span className="text-warn">{t('feedback.esc.nobody')}</span>,
    },
    {
      key: 'status',
      header: t('feedback.queue.status'),
      render: (r) => (
        <StatusBadge tone={r.open ? 'warn' : 'ok'}>{t(`feedback.status.${r.status}` as never)}</StatusBadge>
      ),
    },
    { key: 'open', header: '', render: (r) => <Button onClick={() => void open(r)}>{t('feedback.queue.open')}</Button> },
  ];

  return (
    <div className="space-y-4">
      <Notice tone="info">{t('feedback.esc.hint')}</Notice>
      <Card title={t('feedback.esc.title')}>
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <Field label={t('feedback.esc.reason')}>
            <Select value={reason} onChange={(e) => setReason(e.target.value)}>
              <option value="">{t('feedback.queue.all')}</option>
              {REASONS.map((r) => (
                <option key={r} value={r}>
                  {t(`feedback.esc.reason.${r}` as never)}
                </option>
              ))}
            </Select>
          </Field>
          <label className="flex items-center gap-2 text-sm text-ink">
            <Checkbox checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
            {t('feedback.esc.openOnly')}
          </label>
          <Button onClick={() => void load()}>{t('feedback.queue.refresh')}</Button>
        </div>
        <DataTable loading={loading} rows={items} columns={columns} empty={t('feedback.esc.empty')} rowKey={(r) => r.id} />
      </Card>

      {selected ? (
        <Drawer title={t('feedback.card.title')} onClose={() => setSelected(null)}>
          <FeedbackCard
            siteId={siteId}
            item={selected}
            t={t}
            locale={locale}
            canAnswer={canAnswer}
            onChanged={(next) => {
              setSelected(next);
              void load();
            }}
            onClose={() => setSelected(null)}
          />
        </Drawer>
      ) : null}
    </div>
  );
}
